// Pure helpers for the match page's Coach Feedback section.

// Coach::FeedbackPolicy only generates feedback for competitor tier and up.
export const FEEDBACK_ACCESS_LEVEL = 2;

export const ADMIN_ACCESS_LEVEL = 5;

// How long the profile cards wait after an explicit request before offering it again.
export const REQUEST_WAIT_MS = 2 * 60 * 1000;

// A backend without coaching_feedback_state cannot say it is writing, so the
// card shows pending this long (a generation takes 7-15s), then falls back to
// quiet with Try again.
export const LEGACY_WAIT_MS = 20 * 1000;

// Pending this long with no answer (a feedback_ready lost to a reconnect, or a
// 429 with no job behind it) reads as failed, so Try again comes back. Longer
// than the backend's own 3-minute limit, so the card never gives up first.
export const PENDING_MAX_MS = 4 * 60 * 1000;

const SETTLED = ['no_data', 'nothing_to_say', 'failed'];

// The backend entitles on the max across the user's teams, while /me's
// access_level is the active team's. Mirror the max so a competitor seat on
// another team is not shown the lock.
export function effectiveAccessLevel(user) {
  const levels = [user?.access_level, ...(user?.teams ?? []).map((team) => team?.access_level)];
  return Math.max(0, ...levels.map((level) => Number(level) || 0));
}

export function coachFeedbackEnabledFor(user) {
  return !!user;
}

// overviews[].steam_id is a string from the coach serializer; the auth user's
// may be a number. Compare as strings.
export function viewerOnRoster(overviews, steamId) {
  if (!steamId || !Array.isArray(overviews)) return false;
  return overviews.some((player) => String(player?.steam_id) === String(steamId));
}

// --- Annotated advice (design D2) ---
// impact-score-model opens each reason in the diagnosis with [[N]] (a reason
// may span sentences) and wraps the phrase of the fix that answers it as
// {{phrase|N}}. Text without anchors (older rows, including the short-lived
// sentence-numbered format) renders plain.

const MARKER = /\{\{([^{}|]+)\|([\d,\s]+)\}\}/g;
const ANCHOR = /\[\[(\d+)\]\]\s*/g;

export function stripAnnotations(text) {
  return typeof text === 'string' ? text.replace(MARKER, '$1').replace(ANCHOR, '') : text;
}

// The worker writes two blocks separated by a blank line: the diagnosis with
// the numbers first, the fix last. The fix is what the player acts on, so it
// leads; everything before it is the reasoning behind a toggle.
export function splitFeedback(text) {
  if (typeof text !== 'string') return { advice: '', reasoning: '' };

  const blocks = stripAnnotations(text)
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean);

  if (blocks.length === 0) return { advice: '', reasoning: '' };

  return {
    advice: blocks[blocks.length - 1],
    reasoning: blocks.slice(0, -1).join('\n\n'),
  };
}

// 'hidden'  the viewer did not play in this match (feedback is per player)
// 'ready'   feedback_text is present (the server decided the viewer may see it)
// 'locked'  below competitor tier: the backend never requests feedback
// 'pending' coaching_feedback_state is pending: the worker is writing it
// 'no_data' | 'nothing_to_say' | 'failed'  as coaching_feedback_state says
// 'quiet'   no usable state (older backend, null): pending for LEGACY_WAIT_MS
//           after load or a request (legacyWaiting), quiet after that
// stalled: pending for PENDING_MAX_MS with no answer, shown as failed
export function coachFeedbackState({
  feedback, feedbackState, accessLevel, viewerPlayed, legacyWaiting = false, stalled = false,
}) {
  if (!viewerPlayed) return 'hidden';
  if (feedback?.feedback_text?.trim()) return 'ready';
  if ((accessLevel ?? 0) < FEEDBACK_ACCESS_LEVEL) return 'locked';
  if (SETTLED.includes(feedbackState)) return feedbackState;
  if (feedbackState === 'pending') return stalled ? 'failed' : 'pending';
  if (legacyWaiting) return 'pending';
  return 'quiet';
}

// CoachChannel feedback_ready for this match. Returns true when the page must
// refetch (ok, or no status from an older backend); any other status is the
// new state, set on the match directly.
export function applyFeedbackReady(match, status) {
  if (!status || status === 'ok') return true;
  // eslint-disable-next-line no-param-reassign -- the page's reactive match is the state
  if (match) match.coaching_feedback_state = status;
  return false;
}

// After a Try again was accepted (202/429). An older backend has no state to
// set; useMatchFeedback's LEGACY_WAIT_MS covers it.
export function markFeedbackPending(match) {
  if (!match || !('coaching_feedback_state' in match)) return;
  // eslint-disable-next-line no-param-reassign -- as in applyFeedbackReady
  match.coaching_feedback_state = 'pending';
}

// --- Coach page: profile feedback (GET/POST /coach/profile_feedback) ---

export const PROFILE_PERIODS = [7, 30, 90];
// Matches impact-score-model's 30-day baseline window; the only combination
// (with all maps) that is requested on page load, the rest wait for a click.
export const DEFAULT_PROFILE_PERIOD = 30;

// The `after` filter: the first day of the period as a UTC YYYY-MM-DD, the
// form the backend keys its cache and the cable event on.
export function profileAfterDate(days, now = Date.now()) {
  return new Date(now - days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

// A cached result's status -> the card state; anything unknown reads as error.
export function profileResultState(result) {
  const status = result?.status;
  if (status === 'ok' && result?.feedback_text?.trim()) return 'ready';
  if (status === 'no_data' || status === 'nothing_to_say') return 'empty';
  return 'error';
}

// -> { segments: [{ text, reasons? }], annotated }. Annotated only when every
// reason has a phrase and every phrase names a real, non-empty reason;
// otherwise the card falls back to plain advice + reasoning, so a tooltip can
// never be wrong or empty.
export function parseAnnotatedFeedback(text) {
  const plain = (t) => ({ segments: t ? [{ text: stripAnnotations(t) }] : [], annotated: false });
  if (typeof text !== 'string' || !text.trim()) return plain('');

  const blocks = text.split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean);
  const fix = blocks[blocks.length - 1];
  const diagnosis = blocks.slice(0, -1).join(' ').replace(MARKER, '$1');

  // [[N]] opens reason N, which runs to the next anchor; any text before the
  // first anchor belongs to the first reason.
  const parts = diagnosis.split(/\[\[(\d+)\]\]\s*/);
  const reasons = new Map();
  for (let i = 1; i < parts.length; i += 2) {
    const id = Number(parts[i]);
    const body = (i === 1 ? `${parts[0]} ${parts[2]}` : parts[i + 1]).trim();
    // An anchor with nothing after it is no reason: a phrase naming it falls back.
    if (body) reasons.set(id, [reasons.get(id), body].filter(Boolean).join(' '));
  }
  if (!reasons.size) return plain(fix);

  const segments = [];
  const used = new Set();
  let unknown = false;
  let last = 0;
  [...fix.matchAll(MARKER)].forEach((m) => {
    if (m.index > last) segments.push({ text: fix.slice(last, m.index) });
    const ids = [...new Set(m[2].split(',').map((n) => Number(n.trim())))];
    if (ids.every((id) => reasons.has(id))) {
      ids.forEach((id) => used.add(id));
      const sorted = ids.sort((a, b) => a - b);
      segments.push({ text: m[1], reasons: sorted.map((id) => reasons.get(id)) });
    } else {
      unknown = true;
      segments.push({ text: m[1] });
    }
    last = m.index + m[0].length;
  });
  if (last < fix.length) segments.push({ text: fix.slice(last) });

  if (unknown || used.size !== reasons.size) return plain(fix);
  return { segments, annotated: true };
}

// "2 hours ago" in the app's language, via the platform's Intl.
export function relativeTime(date, locale, now = Date.now()) {
  const seconds = Math.round((date.getTime() - now) / 1000);
  const units = [['day', 86400], ['hour', 3600], ['minute', 60]];
  const [unit, size] = units.find(([, s]) => Math.abs(seconds) >= s) || ['second', 1];
  return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(Math.round(seconds / size), unit);
}
